import { logger } from '@/libs/logger';
import {
    MediaPackProgramsRequestItem,
    MediaProgramsResponseItem,
} from '@calm/evexia-web-sdk';
import { useCircle } from '@rhg/hooks/use-circle';
import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    checkQueryCache,
    ItemToQueryMapping,
    queryClient,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { getRemappedPackClass } from '@rhg/utils/programs';
import { useMemo } from 'react';
import { useActiveCircleId } from '../use-active-circle';

/**
 * Ex. { [packClass]: programIdsSet, ...}
 */
export type PackClassesMapWithProgramIdsSet = Record<string, Set<string>>;
export type RequiredMediaPackProgramsRequestItem =
    Required<MediaPackProgramsRequestItem>;

/**
 * Transforms and filters the input packClassesWithProgramIdsMap to:
 * 1. Remove empty pack classes
 * 2. Remove empty program ID sets
 * 3. Filter out empty program IDs from sets
 *
 * This ensures that all data sent to the API and used in query keys is valid,
 * allowing effective cache invalidation when specific pack class and program ID
 * combinations change. TanStack Query uses this structure for partial key matching
 * during cache invalidation.
 */
export function filterPackClassesWithProgramIdsMap(
    packClassesWithProgramIdsMap: PackClassesMapWithProgramIdsSet
) {
    return Object.entries(
        packClassesWithProgramIdsMap
    ).reduce<PackClassesMapWithProgramIdsSet>(
        (filteredMap, [packClass, programIdsSet]) => {
            // Skip empty pack classes
            if (!packClass) {
                return filteredMap;
            }

            // Filter out empty program IDs
            const filteredProgramIds = Array.from(programIdsSet).filter(
                (programId) => programId.length > 0
            );

            // Only add if we have remaining program IDs
            if (filteredProgramIds.length > 0) {
                return {
                    ...filteredMap,
                    [packClass]: new Set(filteredProgramIds),
                };
            }

            return filteredMap;
        },
        {}
    );
}

function remapBlocklistPackClasses(
    packClassesWithProgramIdsMap: PackClassesMapWithProgramIdsSet
): PackClassesMapWithProgramIdsSet {
    return Object.entries(
        packClassesWithProgramIdsMap
    ).reduce<PackClassesMapWithProgramIdsSet>(
        (filteredMap, [packClass, programIdsSet]) => {
            return {
                ...filteredMap,
                [getRemappedPackClass(packClass)]: programIdsSet,
            };
        },
        {}
    );
}

export function isValidPackClassesMapWithProgramIdsSet(
    packClassesWithProgramIdsMap: PackClassesMapWithProgramIdsSet
) {
    if (Object.keys(packClassesWithProgramIdsMap).length === 0) {
        return false;
    }
    return Object.entries(packClassesWithProgramIdsMap).every(
        ([packClass, programIdsSet]) => {
            if (!packClass) {
                return false;
            }
            if (!programIdsSet.size) {
                return false;
            }
            return Array.from(programIdsSet).every(
                (programId) => programId.length > 0
            );
        }
    );
}

export const programsQueryOptions = {
    /**
     * The key should be a pack class map where each packClass map contains an array of program IDs for easy invalidation
     * This allows us to invalidate any query with a given pack class-program ID combo by passing in [queryKeys.programs, circleId, { somePackClass: [someProgramId] }]
     *
     * Refs:
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation#query-matching-with-invalidatequeries
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-keys#array-keys-with-variables
     */
    queryKey: function ({
        packClassesWithProgramIdsMap,
        circleId,
    }: {
        packClassesWithProgramIdsMap: PackClassesMapWithProgramIdsSet;
        circleId: number;
    }) {
        const remappedPackClassesWithProgramIdsMap = remapBlocklistPackClasses(
            packClassesWithProgramIdsMap
        );
        return [
            queryKeys.programs,
            circleId,
            Object.entries(remappedPackClassesWithProgramIdsMap).reduce(
                (prev, [packClass, programIdsSet]) => ({
                    ...prev,
                    [packClass]: Array.from(programIdsSet).sort(),
                }),
                {}
            ),
        ];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        items,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        items: RequiredMediaPackProgramsRequestItem[];
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getRecipientPrograms({
            circleAuthorizationToken,
            mediaPackProgramsRequest: {
                items,
            },
            recipientId: circleId,
            xDevicePlatform: 'www',
            rHGApp,
        });

        return response.data;
    },
    queryOptions: function ({
        packClassesWithProgramIdsMap,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        packClassesWithProgramIdsMap: PackClassesMapWithProgramIdsSet;
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                isValidPackClassesMapWithProgramIdsSet(
                    packClassesWithProgramIdsMap
                )
            ),
            staleTime: 1000 * 60 * 15, // 15 minutes
        };
    },
};

export function usePrograms(
    packClassesWithProgramIdsMap: PackClassesMapWithProgramIdsSet,
    _circleId?: number
) {
    const { activeCircleId } = useActiveCircleId();
    const circleId = _circleId || activeCircleId;
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = programsQueryOptions;
    const rHGApp = getRhgAppHeader();
    const filteredPackClassesWithProgramIdsMap = useMemo(
        () =>
            remapBlocklistPackClasses(
                filterPackClassesWithProgramIdsMap(packClassesWithProgramIdsMap)
            ),
        [packClassesWithProgramIdsMap]
    );

    const options = useMemo(
        () =>
            queryOptions({
                packClassesWithProgramIdsMap:
                    filteredPackClassesWithProgramIdsMap,
                isAuthenticated,
                circleId,
                circleAuthorizationToken,
            }),
        [
            circleAuthorizationToken,
            circleId,
            filteredPackClassesWithProgramIdsMap,
            isAuthenticated,
            queryOptions,
        ]
    );

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({
            packClassesWithProgramIdsMap: filteredPackClassesWithProgramIdsMap,
            circleId,
        }),
        queryFn: async () => {
            if (
                !isValidPackClassesMapWithProgramIdsSet(
                    filteredPackClassesWithProgramIdsMap
                )
            ) {
                logger.error(
                    'No valid pack classes with program IDs provided',
                    {
                        request: Object.entries(
                            filteredPackClassesWithProgramIdsMap
                        ).map(([packClass, programIdSet]) => ({
                            packClass,
                            programIds: Array.from(programIdSet),
                        })),
                    },
                    new Error('No valid pack classes with program IDs provided')
                );

                return [];
            }

            const keyObjects = Object.entries(
                filteredPackClassesWithProgramIdsMap
            ).reduce<
                ItemToQueryMapping<RequiredMediaPackProgramsRequestItem>[]
            >((existingKeyObjects, [packClass, programIdsSet]) => {
                return [
                    ...existingKeyObjects,
                    ...Array.from(programIdsSet).map<
                        ItemToQueryMapping<RequiredMediaPackProgramsRequestItem>
                    >((programId) => ({
                        item: { packClass, programIds: [programId] },
                        queryKey: programsQueryOptions.queryKey({
                            circleId,
                            packClassesWithProgramIdsMap: {
                                [packClass]: new Set([programId]),
                            },
                        }),
                    })),
                ];
            }, []);

            const { cachedQueries, invalidQueries } = checkQueryCache<
                RequiredMediaPackProgramsRequestItem,
                MediaProgramsResponseItem[]
            >(keyObjects, options.staleTime);

            const cachedQueriesData = cachedQueries.reduce<
                MediaProgramsResponseItem[]
            >((cachedData, item) => [...cachedData, ...item.data], []);

            // If all packs are already fetched and not stale, return them
            if (invalidQueries.length === 0) {
                return cachedQueriesData;
            }

            const packClassProgramsToFetch = invalidQueries.reduce<
                RequiredMediaPackProgramsRequestItem[]
            >((builtQuery, { item }) => {
                // Find if we already have this packClass in our accumulator
                const existingIndex = builtQuery.findIndex(
                    (i) => i.packClass === item.packClass
                );

                if (existingIndex >= 0) {
                    // Merge programIds with existing entry
                    builtQuery[existingIndex].programIds = [
                        ...builtQuery[existingIndex].programIds,
                        ...item.programIds,
                    ];
                } else {
                    // Add new entry
                    builtQuery.push({ ...item });
                }

                return builtQuery;
            }, []);

            const response = await queryFn({
                getAccessTokenSilently,
                items: packClassProgramsToFetch,
                circleId,
                circleAuthorizationToken,
                rHGApp,
            });
            const responseItems = response.items;

            // populate cache for each pack class
            responseItems?.forEach((packResponse) => {
                const { packClassId, programs } = packResponse;
                programs.forEach((program) => {
                    const key = queryKey({
                        circleId,
                        packClassesWithProgramIdsMap: {
                            [packClassId]: new Set([program.id]),
                        },
                    });
                    queryClient.setQueryData<MediaProgramsResponseItem[]>(key, [
                        {
                            ...packResponse,
                            programs: packResponse.programs.filter(
                                (responseProgram) =>
                                    responseProgram.id === program.id
                            ),
                        },
                    ]);
                });
            });

            // Combine newly fetched packs with existing packs
            return [...cachedQueriesData, ...(responseItems || [])];
        },
        ...options,
    });

    return {
        programs: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}
